import { useState, useRef } from 'react';
import InputField from '../../../../components/InputField';
import TextAreaField from '../../../../components/TextAreaField';
import SelectTwo from '../../../../components/SelectTwo';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../../../components/buttons/ButtonCancelForm';

interface Props {
    form: {
        name: string;
        description: string;
        category: string;
        file: File | null;
    };
    handleInputChange: (field: string) => (value: any) => void;
    handleFileChange: (file: File | null) => void;
    handleSubmit: () => void;
    onCancel: () => void;
    loading: boolean;
    fieldErrorMessage: (field: string, isArray?: boolean) => string;
    onFocusRemove: (field: string, isArray?: boolean) => void;
    isEdit: boolean;
    currentFileName?: string;
}

const categoryOptions = [
    { value: 'manual', label: 'Manual de Usuario' },
    { value: 'policy', label: 'Política Interna' },
    { value: 'procedure', label: 'Procedimiento' },
    { value: 'contract', label: 'Contrato' },
    { value: 'faq', label: 'Preguntas Frecuentes' },
    { value: 'legal', label: 'Documento Legal' },
    { value: 'other', label: 'Otro' }
];

const ChatDocumentForm = ({
    form,
    handleInputChange,
    handleFileChange,
    handleSubmit,
    onCancel,
    loading,
    fieldErrorMessage,
    onFocusRemove,
    isEdit,
    currentFileName
}: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileChange(e.target.files[0]);
        }
    };

    const handleRemoveFile = () => {
        handleFileChange(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="row">
                <div className="col-12 mb-3">
                    <InputField
                        label="Nombre del documento"
                        value={form.name}
                        onChange={handleInputChange('name')}
                        onFocus={() => onFocusRemove('name')}
                        error={fieldErrorMessage('name')}
                        placeholder="Ej: Manual de usuario del sistema"
                        required
                    />
                </div>
                
                <div className="col-12 mb-3">
                    <TextAreaField
                        label="Descripción (opcional)"
                        value={form.description}
                        onChange={handleInputChange('description')}
                        onFocus={() => onFocusRemove('description')}
                        error={fieldErrorMessage('description')}
                        placeholder="Describe brevemente el contenido del documento"
                        rows={3}
                    />
                </div>
                
                <div className="col-12 mb-3">
                    <SelectTwo
                        label="Categoría"
                        name="category"
                        inputValue={form.category}
                        options={categoryOptions}
                        onChange={(option: any) => handleInputChange('category')(option?.value || 'other')}
                        placeholder="Seleccionar categoría"
                    />
                </div>
                
                <div className="col-12 mb-3">
                    <label className={`form-label fw-bold ${fieldErrorMessage('file') ? 'text-danger' : ''}`}>
                        Archivo {!isEdit && <span className="text-danger">*</span>}
                    </label>
                    
                    {isEdit && currentFileName && !form.file && (
                        <div className="mb-2 p-2 bg-light rounded d-flex align-items-center">
                            <i className="fas fa-file-alt me-2 text-primary"></i>
                            <span className="text-muted">{currentFileName}</span>
                            <small className="ms-2 text-muted">(archivo actual)</small>
                        </div>
                    )}
                    
                    <div
                        className={`border rounded p-4 text-center ${dragActive ? 'border-primary bg-light' : 'border-dashed'}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        style={{ 
                            borderStyle: 'dashed',
                            cursor: 'pointer',
                            minHeight: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.txt"
                            onChange={handleFileInputChange}
                            style={{ display: 'none' }}
                        />
                        
                        {form.file ? (
                            <div className="d-flex align-items-center gap-2">
                                <i className="fas fa-file-alt fa-2x text-success"></i>
                                <div>
                                    <p className="mb-0 fw-bold">{form.file.name}</p>
                                    <small className="text-muted">
                                        {(form.file.size / 1024 / 1024).toFixed(2)} MB
                                    </small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger ms-3"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        ) : (
                            <>
                                <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-2"></i>
                                <p className="mb-1">
                                    <strong>Arrastra y suelta</strong> un archivo aquí o <strong>haz clic</strong> para seleccionar
                                </p>
                                <small className="text-muted">
                                    Formatos permitidos: PDF, DOC, DOCX, TXT (máx. 20MB)
                                </small>
                            </>
                        )}
                    </div>
                    
                    {fieldErrorMessage('file') && (
                        <div className="text-danger mt-1 small">
                            {fieldErrorMessage('file')}
                        </div>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
                <ButtonCancelForm callbackCancel={onCancel} />
                <ButtonSaveForm 
                    callbackSave={handleSubmit} 
                    loading={loading}
                    title={isEdit ? 'Actualizar' : 'Crear documento'}
                />
            </div>
        </form>
    );
};

export default ChatDocumentForm;

